"use client"; // Ensure this line is included

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { X } from "lucide-react";
import { useGetAdsPopupQuery } from "@/redux/features/api/home/homeApiSlice";
import { useTranslate } from "../base/providers/TranslateProv";
import { usePathname } from "next/navigation";

const PopupAd: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [nextShowTime, setNextShowTime] = useState<number | null>(null);
  const { data, refetch } = useGetAdsPopupQuery({});
  const pathname = usePathname();
  const { locale }: { locale: "ar" | "en" } = useTranslate();
  const adTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get the current time in milliseconds
  const now = Date.now();

  // Set initial show time to 30 seconds from mount
  useEffect(() => {
    if (nextShowTime === null) {
      setNextShowTime(Date.now() + 30000);
    }
  }, []);

  useEffect(() => {
    // Calculate the time when the ad should next appear
    const calculateNextShowTime = () => {
      if (isClosed && nextShowTime) {
        const remainingTime = Math.max(0, nextShowTime - now);
        return remainingTime;
      }
      if (isVisible) {
        return 0; // Ad is currently visible
      }
      if (nextShowTime) {
        return Math.max(0, nextShowTime - now);
      }
      return 30000; // 30 seconds for initial ad display
    };

    const remainingTime = calculateNextShowTime();

    if (remainingTime > 0) {
      adTimeoutRef.current = setTimeout(() => {
        setIsVisible(true);
        if (isClosed) {
          setIsClosed(false);
          setNextShowTime(Date.now() + 420000); // 7 minutes from now
        }
      }, remainingTime);
    }

    return () => {
      if (adTimeoutRef.current) {
        clearTimeout(adTimeoutRef.current);
      }
    };
  }, [isVisible, isClosed, nextShowTime, pathname]);

  const handleClose = () => {
    setIsVisible(false);
    setIsClosed(true);
    setNextShowTime(Date.now() + 420000); // 7 minutes from now
  };

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
    event.stopPropagation();
  };

  // Return null for routes where the ad should not appear
  if (pathname.includes("/ar/dashboard") || pathname.includes("/en/dashboard")) {
    return null;
  }

  return (
    <>
      {data?.data && data?.data?.image && isVisible && (
        // {data?.data && data?.data?.image && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[1500]"
          style={{ pointerEvents: isVisible ? "auto" : "none" }} // Ensure clicks go through when visible
        >
          <div className="relative bg-white   p-[20px] w-[90%] min-w-[300px] min-h-[300px] lg:max-w-[500px] h-auto rounded-[15px] shadow-lg mx-auto z-[1600]">
            <div className="flex items-center justify-between w-full mb-[20px]">
              <span className="text-[14px] font-[400] font-somar-sans px-[13px] py-[4px] rounded-full text-[#6C7278] bg-[#6C7278]/15 ">
                {locale === "ar" ? "إعلان هام" : "Important Ad"}
              </span>
              <div onClick={handleClose} className=" rounded-[8px] cursor-pointer z-[1700] ">
                <div className="w-[30px] h-[30px] flex items-center justify-center cursor-pointer">
                  <X size={20} className="text-[#6C7278]" />
                </div>
              </div>
            </div>

            <Image
              src={data?.data?.image ? data?.data?.image[locale] : ""}
              alt="PopupAd"
              width={500}
              height={300}
              className="rounded-[18px] "
              onClick={handleImageClick}
            />
            <div className="mt-[23px] cursor-pointer h-[58px] text-white text-[20px] flex items-center justify-center text-center bg-[#1F0570] rounded-[10px] w-full ">
              <a className="w-full" href={data?.data?.link ? data?.data?.link[locale] : ""} target="_blank">
                {locale === "ar" ? "تعرف علي المزيد" : "Learn More"}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PopupAd;
